import React from 'react'
import _ from 'lodash'
import {formatText} from 'lib/textUtils'
import {Topo} from 'common/styleguide/Topo'
import {Card} from 'common/styleguide/Card'
import {Icon} from 'common/Icon'
import {connect} from 'react-redux'
import {Loadable} from 'common/Loadable'
import {getExperienceId} from '../ExperiencesApp/ExperiencesApp'
import styles from './ExperienceShow.scss'
import cx from 'classnames'
import {ITUNES_APP_URL, GPLAY_APP_URL, promiseMapAll} from 'lib/utils'
import {experienceActions, experienceSelectors} from 'data/resources/experiences'
import {clubSelectors} from 'data/resources/clubs'
import {experienceRoutesActions} from 'lib/api/experiences/routes'
import {Experience} from 'data/records'
import {List} from 'immutable'
import {RouteWidget} from './RouteWidget'
import QRCode from 'qrcode.react'
import Helmet from 'react-helmet'

export const DEFAULT_CONFIG = {
  showDate: true,
  showLocation: true,
  showDescription: true,
  allowDownload: true
}

export class ExperienceShow extends React.PureComponent {
  componentDidMount () {
    const {club} = this.props
    const primaryColor = _.get(club, 'appDetails.primary_color')
    if (primaryColor) {
      this._primaryColorOverrideStyle = document.createElement('style')
      document.head.appendChild(this._primaryColorOverrideStyle)
      const sheet = this._primaryColorOverrideStyle.sheet
      ;['a', 'a:visited', 'a:hover', 'a:active', 'a:focus'].forEach(sel => {
        sheet.insertRule(`.${styles.ExperienceShow} ${sel} { color: ${primaryColor} }`, 0)
      })
    }
  }

  componentWillUnmount () {
    if (this._primaryColorOverrideStyle) {
      document.head.removeChild(this._primaryColorOverrideStyle)
    }
  }

  render () {
    const {experience, club, routeTuples, appCode} = this.props
    const config = this.props.config || experience.viewConfig || DEFAULT_CONFIG

    const appName = _.get(club, 'appDetails.app_name', 'Ride with GPS')
    const itunesUrl = _.get(club, 'appDetails.itunes_url', ITUNES_APP_URL)
    const gplayUrl = _.get(club, 'appDetails.play_store_url', GPLAY_APP_URL)

    return (
      <div className={styles.ExperienceShow}>
        <Helmet title={experience.name} />
        <Topo dark style={{padding: '40px 0'}}>
          <div className={cx(styles.innerCentered, styles.topoInner)}>
            <div style={{maxWidth: '420px'}}>
              <h2 data-heading-alt style={{color: '#FFF', marginBottom: '15px'}}>Mobile</h2>
              <h1 style={{marginBottom: '20px', fontSize: '26px'}}>Navigate your routes and access all your information on mobile</h1>
              <ol className={styles.customOl}>
                <li><span>Download the {appName} mobile app for free via the <a href={itunesUrl}>iTunes App Store</a> or <a href={gplayUrl}>Google Play Store</a></span></li>
                <li><span>Tap Open Shortcut</span></li>
                <li><span>Enter shortcut or scan the QR</span></li>
                <li><span>You'll be prompted to download the Experience. Tap Download to begin.</span></li>
              </ol>
              <div style={{fontSize: '13px'}}>
                For more information on mobile access and features, see the <a href='https://ridewithgps.com/help/mobile-app-experiences/'>mobile help page</a>.
              </div>
            </div>
            <div className={styles.codeContainer}>
              <div className={styles.appCode}>
                {appCode.code.toUpperCase()}
              </div>
              <QRCode className={styles.qrCode} value={`rwgps://app/code/${appCode.code.toUpperCase()}`} size={126} level='M' />
            </div>
          </div>
        </Topo>
        <div className={cx(styles.innerCentered, styles.main)}>
          <h2 data-heading-alt>Overview</h2>
          <Card>
            <div className={styles.logoContainer}>
              <div>
                <img className={styles.logo} src={experience.logo} />
              </div>
              <div>
                <h1 style={{margin: '0 0 9px 0'}}>{experience.name}</h1>
                {config.showDate && experience.startsOn && <div className={styles.experienceDetail}>
                  <Icon i='md-calendar' size={24} className={styles.greyIcon} />
                  {Experience.formatDate(experience)}
                </div>}
                {config.showLocation && experience.location && <div className={styles.experienceDetail}>
                  <Icon i='md-pin' size={24} className={styles.greyIcon} />
                  {experience.location}
                </div>}
              </div>
            </div>
            {config.showDescription && experience.description && <div className={styles.description}>
              {formatText(experience.description, {autolink: true})}
            </div>}
          </Card>

          <h2 data-heading-alt>Routes</h2>
          <Card noPadding>
            <RouteWidget experience={experience} allowDownload={config.allowDownload} appCode={appCode} routeTuples={routeTuples} />
          </Card>
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  const id = getExperienceId(ownProps)
  // [route, experienceRoute] ordered list
  const routeTuples = state.routes.getIn(['orderedExperienceRouteIds', id], List())
    .map(experienceRouteId => {
      const experienceRoute = state.routes.getIn(['experienceRoutes', experienceRouteId])
      const route = state.routes.getIn(['routes', experienceRoute.routeId])
      return [route, experienceRoute]
    })
  const experience = experienceSelectors.find(state, id)
  return {
    experienceId: id,
    experience,
    club: experience ? clubSelectors.find(state, experience.clubId) : null,
    appCode: experienceSelectors.appCode(state, id),
    routeTuples
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const id = getExperienceId(ownProps)
  const {app_code: appCode} = ownProps.location.query
  return {
    loadExperience: () => dispatch(experienceActions.read(id, {appCode, extras: 'club'})),
    // params dont convert to snakecase because experienceRouteActions doesnt use apiv3 helpers
    loadRoutes: () => dispatch(experienceRoutesActions.loadAll(id, {app_code: appCode})),
    loadAppCode: () => dispatch(experienceActions.appCode(id, {appCode})),
    experienceId: id
  }
}

const _LoadableExperienceShow = ({loadExperience, loadOrganization, loadRoutes, loadAppCode, ...props}) => {
  const resourceLoaded = () => promiseMapAll({
    experience: loadExperience(),
    routes: loadRoutes(),
    appCode: loadAppCode()
  })
  return <Loadable
    component={ExperienceShow}
    resourceLoaded={resourceLoaded}
    {...props} />
}

export const LoadableExperienceShow = connect(
  mapStateToProps,
  mapDispatchToProps
)(_LoadableExperienceShow)
